// Companion/ChatSuggestedPrompts.tsx
import {
  BookOpen,
  ChartLineUp,
  Question,
  Sparkle,
} from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { AIActive } from '@/assets/svgs/AIActive';
import { useIsCourseStudent } from '@/instances/useInstance';
import { cn } from '@/lib/utils';
import type { RootState } from '@/store/store';
import type { ChatAnchorInput, ChatRouteId } from '@/types/chatEnvelope';
import { examsSelectors } from '@/slices/exams/examsSlice';
import {
  getQuestionPromptLabel,
  isPreclinicalModeExam,
} from '@/utils/examUtils';
import ChatChip, { ChatChipRow } from './ChatChip';
import {
  getHelpPrompts,
  getStudyPresetPrompts,
  getSurfacePrompts,
  HELP_PRESET_PROMPTS,
  PERFORMANCE_PRESET_PROMPTS,
  hasAnchorPrompts,
  type ChatExamMode,
} from './surfacePrompts';
import { useIsMcGrawHill } from '../../../../mcgrawHill/mcgrawHill'; // mcgraw-hill
import { MCGRAW_HILL_CHAPTER_PROMPTS } from '../../../../mcgrawHill/mcgrawHillChapter'; // mcgraw-hill
import { useIsMcGrawHillClinical } from '../../../../mcgrawHill/mcgrawHillModeStore'; // mcgraw-hill

/**
 * Every string this surface shows, in one place, so the copy can be edited
 * without reading the layout.
 */
const COPY = {
  greeting: 'Hi, what are we working on?',
  greetingWithName: (firstName: string) =>
    `Hi ${firstName}, what are we working on?`,
  // A course student's companion sits beside their module: no "what are we
  // working on", the module is the work.
  courseGreeting: 'Hi, how can I help?',
  courseGreetingWithName: (firstName: string) =>
    `Hi ${firstName}, how can I help?`,
};

/**
 * The legacy panel's three preset groups, strings unchanged (ORA-509). They
 * match the companion's V1 action set, so they are reused rather than
 * re-authored until the legacy panel is retired.
 *
 * Only Help varies by exam mode (ORA-628): asking for questions or for a weak
 * topic means the same thing in either workspace, while "what is the Daily
 * Session?" is a board-exam question a Preclinical student never asks. The
 * questions chip names the exam (ORA-952): NCLEX for a nursing student, USMLE
 * for everyone else.
 */
const suggestedPromptGroups = (
  examMode: ChatExamMode,
  questionPromptLabel: string
) =>
  [
    { ...getStudyPresetPrompts(questionPromptLabel), Icon: BookOpen },
    { ...PERFORMANCE_PRESET_PROMPTS, Icon: ChartLineUp },
    {
      label: HELP_PRESET_PROMPTS.label,
      prompts: getHelpPrompts(examMode),
      Icon: Question,
    },
  ] as const;

/** Each chip enters a beat after the one before it. */
const CHIP_STAGGER_MS = 60;

/**
 * Where each group's chips start in the one staggered sweep, so the cascade
 * reads as a single pass rather than three that restart.
 */
/**
 * The student's first name, or nothing.
 *
 * Read defensively: both shells also mount under the narrow chat-only test
 * store, where there is no user slice at all, and an anonymous greeting is the
 * right answer there rather than a crash.
 */
const selectFirstName = (state: Partial<RootState>): string | undefined => {
  const name = state.user?.profile?.name?.trim();
  return name ? name.split(/\s+/)[0] : undefined;
};

/**
 * Which workspace the student is studying in, from their primary exam.
 *
 * Read as defensively as the greeting above: the narrow chat-only test store
 * carries no exams slice, and the answer there is `unknown` rather than a
 * crash — which shows the same board-exam copy the surface showed before.
 */
const selectExamMode = (state: Partial<RootState>): ChatExamMode => {
  const primaryExam = state.exams
    ? examsSelectors.selectPrimaryExam(state as RootState)
    : undefined;

  if (!primaryExam) return 'unknown';
  return isPreclinicalModeExam(primaryExam) ? 'course' : 'session';
};

/**
 * The exam named in the "Give me ___ questions" chip, read as defensively as
 * the exam mode: with no exams slice it is USMLE, the copy shown before.
 */
const selectQuestionPromptLabel = (state: Partial<RootState>): string =>
  getQuestionPromptLabel(
    state.exams ? examsSelectors.selectPrimaryExam(state as RootState) : null
  );

export interface ChatSuggestedPromptsProps {
  /** Send a chip through the one send path, the way a follow-up chip does. */
  onSend: (prompt: string) => void;
  /** The dedicated chat page puts the Ora logo above the composer instead. */
  showGreeting?: boolean;
  /** Named route already resolved by the shell's app-context envelope. */
  route?: ChatRouteId;
  /** Content the student is currently looking at, when there is one. */
  anchor?: ChatAnchorInput | null;
  /**
   * How the chips fill their row. The wide chat page wraps them; the drawer
   * and the document split pane stack them one per line, because at that width
   * wrapping leaves a ragged row of one-and-a-half chips (ORA-628). The AI
   * desktop lays the groups out as side-by-side columns, each a vertical
   * list, from lg up — and shows none below lg, where the nav rail and the
   * history rail leave no room for them.
   */
  layout?: 'wrap' | 'stack' | 'columns';
}

export interface ChatSuggestedPromptsGreetingProps {
  headingLevel?: 'h1' | 'h2';
  centered?: boolean;
  headingClassName?: string;
}

/** The one greeting shared by the page and drawer empty states. */
export const ChatSuggestedPromptsGreeting = ({
  headingLevel: Heading = 'h2',
  centered = false,
  headingClassName,
}: ChatSuggestedPromptsGreetingProps) => {
  const firstName = useSelector(selectFirstName);
  const isCourseStudent = useIsCourseStudent();

  const greeting = isCourseStudent
    ? firstName
      ? COPY.courseGreetingWithName(firstName)
      : COPY.courseGreeting
    : firstName
      ? COPY.greetingWithName(firstName)
      : COPY.greeting;

  return (
    <div
      className={cn(
        'animate-chat-fade-up mb-7 flex flex-col',
        centered && 'items-center text-center'
      )}
    >
      {/* The companion's own mark leads a course student's greeting. */}
      {isCourseStudent && (
        <span
          className="mb-3 text-fg-primary [&>svg]:size-10"
          aria-hidden="true"
          data-testid="chat-greeting-mark"
        >
          <AIActive />
        </span>
      )}
      <Heading
        className={cn(
          'font-heading text-lg font-medium text-fg-primary',
          headingClassName
        )}
      >
        {greeting}
      </Heading>
    </div>
  );
};

/**
 * The empty-conversation greeting and preset chips (ORA-603).
 *
 * A short greeting says whose companion this is, and the three groups below it
 * say what it can be asked. The chips are the same `ChatChip` the follow-ups
 * use, so an empty thread and a finished answer speak the same language. A
 * group wraps or stacks by `layout` from `mobile` up, so a narrow drawer never
 * leaves a ragged half-row.
 *
 * Below `mobile` every group is one sideways-scrolling line instead (ORA-628).
 * A phone showing three stacked groups of full-width chips was showing the
 * presets in place of the thread and the composer; one line each says the same
 * thing and leaves the screen to the conversation.
 */
const ChatSuggestedPrompts = ({
  onSend,
  showGreeting = true,
  route = 'other',
  anchor = null,
  layout = 'wrap',
}: ChatSuggestedPromptsProps) => {
  const examMode = useSelector(selectExamMode);
  const questionPromptLabel = useSelector(selectQuestionPromptLabel);
  const isCourseStudent = useIsCourseStudent();
  const isMcGrawHill = useIsMcGrawHill();
  const isMcGrawHillClinical = useIsMcGrawHillClinical(isMcGrawHill);

  // mcgraw-hill: a chapter gets its own starters, none of the route's.
  const surfacePrompts =
    isMcGrawHill && anchor?.type === 'lecture'
      ? MCGRAW_HILL_CHAPTER_PROMPTS
      : getSurfacePrompts(route, anchor, examMode);

  const presetGroups = suggestedPromptGroups(examMode, questionPromptLabel);
  // When the student is looking at a specific piece of content, the anchored
  // chips are the whole offer — "How am I doing in QBank?" next to a document
  // split pane or a study drawer is noise. Route-only surfaces (the dashboard,
  // performance, a landing page) keep the generic groups underneath.
  //
  // A course student (Courses V1) gets no chips at all: the preset groups
  // (QBank score, weakest topic, Daily Session) and the page chips ("what is
  // left in my lectures?") are written for the default experience.
  //
  // mcgraw-hill: in Clinical mode no Study / Performance / Help groups
  // either, only the page's own chips (the desktop shows none). Study mode
  // offers them as everyone else gets them (ORA-1265).
  const shownPresetGroups = isMcGrawHillClinical ? [] : presetGroups;

  const groups = isCourseStudent
    ? []
    : surfacePrompts.length === 0
      ? shownPresetGroups
      : [
          { label: 'On this page', prompts: surfacePrompts, Icon: Sparkle },
          ...(hasAnchorPrompts(anchor) ? [] : shownPresetGroups),
        ];

  const hasGroups = groups.length > 0;

  const groupChipOffsets = groups.map((_, index) =>
    groups
      .slice(0, index)
      .reduce((total, group) => total + group.prompts.length, 0)
  );

  return (
    <div
      className={cn(
        'chat-motion flex w-full flex-col gap-6 max-mobile:gap-3',
        // The columns layout is measured by its own text (the empty page sizes
        // the composer to match it), so it must not be clamped to a width its
        // chips would then wrap inside.
        layout === 'columns' ? 'lg:w-max lg:max-w-full' : 'max-w-3xl'
      )}
      data-testid="chat-suggested-prompts"
      data-layout={layout}
    >
      {showGreeting ? <ChatSuggestedPromptsGreeting /> : null}

      {hasGroups && (
        <div
          className={cn(
            'flex flex-col gap-4 max-mobile:gap-3',
            // Each column is as wide as its own longest question and the row is
            // as wide as the three together, so the gutters are the gap and
            // nothing else. Equal thirds inside a fixed width put each column's
            // leftover space in a different place, which read as three different
            // gaps — and squeezed Preclinical's longer Help questions onto two
            // lines apiece.
            // Below lg the desktop drops the lists altogether: between the nav
            // rail and the history rail there is no width to spare for them,
            // and stacked they pushed the composer off the screen.
            layout === 'columns' &&
              'max-lg:hidden lg:flex-row lg:items-start lg:gap-10'
          )}
          data-testid="chat-suggested-prompt-groups"
        >
          {groups.map(({ Icon, label, prompts }, groupIndex) => (
            <div
              key={label}
              className={cn(
                'flex flex-col gap-2',
                layout === 'columns' && 'md:gap-1 lg:min-w-0'
              )}
              role="group"
              aria-label={`${label} suggestions`}
            >
              <div className="text-xs font-medium tracking-wide text-fg-secondary uppercase opacity-70">
                {label}
              </div>
              <ChatChipRow
                className={cn(
                  layout === 'stack' && 'flex-col items-start',
                  layout === 'wrap' && 'flex-wrap',
                  layout === 'columns' &&
                    'flex-wrap md:flex-col md:flex-nowrap md:items-start md:gap-0.5'
                )}
              >
                {prompts.map((prompt, promptIndex) => {
                  const delayMs =
                    (groupChipOffsets[groupIndex] + promptIndex) *
                    CHIP_STAGGER_MS;

                  return (
                    <ChatChip
                      variant={layout === 'columns' ? 'plain' : 'pill'}
                      key={prompt}
                      className="animate-chat-fade-up"
                      style={{ animationDelay: `${delayMs}ms` }}
                      icon={<Icon size={14} aria-hidden="true" />}
                      onClick={() => onSend(prompt)}
                    >
                      {prompt}
                    </ChatChip>
                  );
                })}
              </ChatChipRow>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatSuggestedPrompts;
